import React, {useLayoutEffect} from "react";
import {useWalletStore} from "../providers/userWallet.store";
import {axiosInstance, refreshAxiosInstance} from "../api/axiosInstance";
import {ResendOtpContentType, SignInFormContentType, SignUpFormContentType, VerifyOtpContentType} from "../types/auths";
import {CustomHelper} from "../helpers";
import {OtpPurposeEnum} from "../types/enums";

const useAuthenticationProviderHook = () => {
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [userInfo, setUserInfo] = React.useState({});
    const [isLoading, setIsLoading] = React.useState(false);
    const [accessToken, setAccessToken] = React.useState<string | null>(null);
    const [isAppInitialized, setIsAppInitialized] = React.useState(false);
    const isInitializingRef = React.useRef(false);
    const {setBalance} = useWalletStore.getState()

    useLayoutEffect(() => {
        const initializeApp = async () => {
            if (isInitializingRef.current || isAppInitialized) {
                return;
            }

            isInitializingRef.current = true;
            try {
                const success = await initializeAuth();
                setIsAuthenticated(success);
            } catch (error) {
                setIsAuthenticated(false);
            } finally {
                setIsAppInitialized(true);
                isInitializingRef.current = false;
            }
        };
        initializeApp();
    }, []);

    const clearAuthData = () => {
        setUserInfo({});
        setIsAuthenticated(false);
        setAccessToken(null)
    };
    const fetchUserInfo = async (token?: string) => {
        try {
            const authToken = token || accessToken
            const headers = {
                Authorization: `Bearer ${authToken}`
            }
            const [userResp, walletResp] = await Promise.all([
                axiosInstance.get('/users/me', {headers}),
                axiosInstance.get('/wallet/balance', {headers}),
            ])
            const userData = userResp.data.data || userResp.data;
            setBalance(walletResp.data?.balance || 0)
            setUserInfo(userData);
            return userData;
        } catch (err: any) {
            console.error(err)
            clearAuthData();
            throw err;
        }
    };

    const initializeAuth = async () => {
        if (accessToken) {
            try {
                await fetchUserInfo();
                return true;
            } catch {
                return false;
            }
        } else {
            try {
                const refresh_resp = await refreshAxiosInstance.post("/auth/token/refresh", {})
                const newAccessToken = refresh_resp.data.data.accessToken

                setAccessToken(newAccessToken)
                await fetchUserInfo(newAccessToken);

                return true;
            } catch (e) {
                console.error(e)
                clearAuthData()
                return false;
            }
        }
    };

    const login = async (formInfo: SignInFormContentType) => {
        setIsLoading(true);
        try {
            const result = await axiosInstance.post('/auth/login', formInfo);
            const data = result.data;
            return {
                success: true,
                data,
                status: result.status,
                message: typeof data.message === "object" ? data.message[0] : data.message,
            }
        } catch (err: any) {
            if (!err.message) err.message = "An error occurred";
            return CustomHelper.throwErrorResponse(err);

        } finally {
            setIsLoading(false);
        }
    }
    const register = async (formInfo: SignUpFormContentType) => {
        setIsLoading(true);
        try {
            const result = await axiosInstance.post('/auth/register', formInfo);
            const data = result.data;
            return {
                success: true,
                data,
                status: result.status,
                message: typeof data.message === "object" ? data.message[0] : data.message,
            }
        } catch (err: any) {
            if (!err.message) err.message = "An error occurred";
            return CustomHelper.throwErrorResponse(err);
        } finally {
            setIsLoading(false);
        }
    }
    const verifyActivateAccountOtp = async (formInfo: VerifyOtpContentType) => {
        setIsLoading(true)
        try {
            const response = await axiosInstance.post('/auth/activate_account', formInfo);
            const data = response.data;
            return {
                data,
                success: true,
                status: response.status,
                message: typeof data.message === "object" ? data.message[0] : data.message,
            }
        } catch (err: any) {
            if (!err.message) err.message = "An error occurred";
            return CustomHelper.throwErrorResponse(err);

        } finally {
            setIsLoading(false);
        }
    }
    const verifyTwoFaOtp = async (formInfo: { purpose: string } & SignInFormContentType) => {
        setIsLoading(true)
        try {
            const response = await axiosInstance.post('/auth/login', formInfo);
            const data = response.data;
            return {
                data,
                success: true,
                status: response.status,
                message: typeof data.message === "object" ? data.message[0] : data.message,
            }
        } catch (err: any) {
            if (!err.message) err.message = "An error occurred";
            return CustomHelper.throwErrorResponse(err);

        } finally {
            setIsLoading(false);
        }
    }
    const verifyPasswordResetOtp = async (formInfo: VerifyOtpContentType & {
        password: string,
        confirmPassword: string
    }) => {
        setIsLoading(true)

        try {
            const response = await axiosInstance.post('/auth/reset_password', {
                ...formInfo,
                purpose: OtpPurposeEnum.RESET_PASSWORD,
            });
            const data = response.data;
            return {
                data,
                success: true,
                status: response.status,
                message: typeof data.message === "object" ? data.message[0] : data.message,
            }
        } catch (err: any) {
            if (!err.message) err.message = "An error occurred";
            return CustomHelper.throwErrorResponse(err);

        } finally {
            setIsLoading(false);
        }
    }
    const resendOtp = async (formInfo: ResendOtpContentType) => {
        setIsLoading(true)
        try {
            const result = await axiosInstance.post('/auth/resend/email', formInfo);
            const data = result.data;
            return {
                success: true,
                data,
                status: result.status,
                message: typeof data.message === "object" ? data.message[0] : data.message,
            }
        } catch (err: any) {
            if (!err.message) err.message = "An error occurred";
            return CustomHelper.throwErrorResponse(err);

        } finally {
            setIsLoading(false);
        }
    }
    const forgotPassword = async (email: string) => {
        setIsLoading(true);
        try {
            const result = await axiosInstance.post('/auth/forget_password', {
                email,
                purpose: OtpPurposeEnum.RESET_PASSWORD,
                accountType: 'individual',
            });
            const data = result.data;
            return {
                success: true,
                status: result.status,
                data,
                message: typeof data.message === "object" ? data.message[0] : data.message,
            }
        } catch (err: any) {
            if (!err.message) err.message = "An error occurred";
            return CustomHelper.throwErrorResponse(err);

        } finally {
            setIsLoading(false);
        }
    }
    return {
        login,
        verifyPasswordResetOtp,
        verifyActivateAccountOtp,
        verifyTwoFaOtp,
        resendOtp,
        isAuthenticated,
        setIsAuthenticated,
        isLoading,
        register,
        forgotPassword,
        customerInfo: userInfo,
        accessToken,
        setAccessToken,
        fetchUserInfo,
        initializeAuth,
        isAppInitialized,
        clearAuthData,
    }
}
export default useAuthenticationProviderHook